import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  StandardDialog,
  StandardDialogBody,
  StandardDialogContent,
  StandardDialogDescription,
  StandardDialogFooter,
  StandardDialogHeader,
  StandardDialogTitle,
} from '@/components/ui/standard-dialog';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useOrgBasePath, useOrgNavigate } from '@/hooks/useOrgNavigate';
import { useCurrentUserOrgRole } from '@/hooks/useCurrentUserOrgRole';
import {
  useAvailableJurisdictions,
  useFullTaxSettings,
  useUpsertTaxSettings,
} from '@/hooks/useOrganizationTaxSettings';
import { getJurisdictionLabel } from '@/hooks/useJurisdictionTaxConfig';
import { toast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Gates the accounting section on the organization's tax setup.
 *
 * Every accounting flow (invoices, expenses, bank feed promotes) depends on
 * the org's tax jurisdiction: it drives the tax_rates set (created by the
 * organization_tax_settings insert trigger), the default VAT auto-fill, and
 * the VAT GL accounts. Without it the tax dropdowns render empty/disabled and
 * postings silently get no tax — so instead of letting users wander into a
 * half-broken section, this wrapper overlays a blocking dialog until
 * `organization_tax_settings.primary_tax_jurisdiction` exists.
 *
 * Admins can complete the setup inline (jurisdiction only — the other tax
 * settings keep the same defaults the full settings page uses); non-admins
 * are told to contact an admin. Dismissing the dialog (X / Esc) leaves the
 * section to the dashboard, so accounting stays unreachable until configured.
 * The children render behind the overlay; queries under them are org-scoped
 * reads that are safe (just empty) without tax config.
 */
export function AccountingSetupGate({ children }: { children: React.ReactNode }) {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;
  const { data: settings, isLoading } = useFullTaxSettings(organizationId);

  const isConfigured = !!settings?.primary_tax_jurisdiction;
  // Only gate once the settings query resolved — no dialog flash while loading.
  const needsSetup = !!organizationId && !isLoading && !isConfigured;

  return (
    <>
      {children}
      {/* Conditionally mounted so the dialog's data hooks only run when shown.
          Keyed by org so a switch to another unconfigured org resets the
          dialog's local jurisdiction selection instead of carrying it over. */}
      {needsSetup && (
        <TaxSetupRequiredDialog key={organizationId} organizationId={organizationId} />
      )}
    </>
  );
}

function TaxSetupRequiredDialog({ organizationId }: { organizationId: string }) {
  const { t } = useTranslation();
  const orgNavigate = useOrgNavigate();
  const orgBasePath = useOrgBasePath();

  // Only an accounting admin (admin or accountant) may save tax settings —
  // the same `accounting_admin` scope the API and RLS check.
  const { can } = useCurrentUserOrgRole();
  const isAdmin = can('accounting_admin');

  const { data: jurisdictions } = useAvailableJurisdictions();
  const upsertMutation = useUpsertTaxSettings();
  const [jurisdiction, setJurisdiction] = useState('');

  const handleSave = () => {
    if (!jurisdiction) return;
    // Same defaults as the full TaxSettings form; the insert trigger creates
    // the jurisdiction's tax rates + VAT GL accounts, and the mutation's
    // invalidation refetches useFullTaxSettings, which unmounts this dialog.
    upsertMutation.mutate(
      {
        organizationId,
        data: {
          primary_tax_jurisdiction: jurisdiction,
          fiscal_year_start_month: 1,
          tax_calculation_method: 'invoice_based',
        },
      },
      {
        onSuccess: () => toast.successTemplates.created('Tax settings'),
        onError: () => toast.errorTemplates.update('tax settings'),
      },
    );
  };

  return (
    <StandardDialog
      open
      onOpenChange={(open) => {
        // The gate is blocking: closing doesn't reveal accounting, it leaves it.
        if (!open) orgNavigate('/dashboard');
      }}
    >
      <StandardDialogContent size="lg" onInteractOutside={(e) => e.preventDefault()}>
        <StandardDialogHeader>
          <StandardDialogTitle>{t('tax.setupGate.title')}</StandardDialogTitle>
          <StandardDialogDescription>
            {t('tax.setupGate.description')}
          </StandardDialogDescription>
        </StandardDialogHeader>
        <StandardDialogBody className="space-y-4">
          {isAdmin ? (
            <div className="space-y-2">
              <Label>{t('tax.ui.settings.jurisdiction')} *</Label>
              <Select value={jurisdiction} onValueChange={setJurisdiction}>
                <SelectTrigger>
                  <SelectValue placeholder={t('tax.ui.settings.jurisdictionPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {jurisdictions?.map((code) => (
                    <SelectItem key={code} value={code}>
                      {getJurisdictionLabel(code)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t('tax.setupGate.jurisdictionHint')}
              </p>
            </div>
          ) : (
            <Alert>
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription>{t('tax.ui.settings.adminWarning')}</AlertDescription>
            </Alert>
          )}
          <p className="text-sm text-muted-foreground">
            <Link to={`${orgBasePath}/settings/tax`} className="underline">
              {t('tax.setupGate.fullSettingsLink')}
            </Link>
          </p>
        </StandardDialogBody>
        <StandardDialogFooter>
          <Button variant="outline" onClick={() => orgNavigate('/dashboard')}>
            {t('tax.setupGate.backToDashboard')}
          </Button>
          {isAdmin && (
            <Button onClick={handleSave} disabled={!jurisdiction || upsertMutation.isPending}>
              {upsertMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('tax.setupGate.save')}
            </Button>
          )}
        </StandardDialogFooter>
      </StandardDialogContent>
    </StandardDialog>
  );
}
